/**
 * Copyright (c) Microsoft. All rights reserved.
 */

/* eslint-disable no-labels */
import { LabelType } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { createEntityLabelStore } from 'src/luis/projects/utterances/stores/entityLabelStore';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';

export type LabelingInteractionsEntity = { name: string; id: LuisId; typeId: ModelType; children?: LabelingInteractionsEntity[] };
export type LabelingInteractionsEntityLabel = { start: number; end: number; entityId: string; labelType?: LabelType; roleId?: string };

export const NEGATIVE_ENTITY_ID = 'NEGATIVE_ENTITY_ID';

// The map value is optional for the following reasons:
// 1) Composite children with roles, have their ids equal to role ids.
// 2) Hierarchical children, don't inherit from entity stores, hence they are not
// included in the schema.
// 3) Entity that the label is associated with is deleted.
export type EntityCollection = Map<LuisId, { entity: LabelingInteractionsEntity; parentId: LuisId } | undefined>;

/**
 * userAction: User has added this label.
 * inferred: Algorithm inferred this label based on the user action.
 * existing: Already existing label.
 */
export type InternalLabelKind = 'userAction' | 'inferred' | 'existing';
export type InternalLabel = {
    originalLabel: LabelingInteractionsEntityLabel;
    children: InternalLabel[];
    kind: InternalLabelKind;
    hierarchicalParentId?: string;
};

export const cutLabel = (label: InternalLabel, [start, end]: [number, number]) => {
    const firstCut = [label.originalLabel.start, start - 1];
    const secondCut = [start, end];
    const thirdCut = [end + 1, label.originalLabel.end];

    return <const>[firstCut, secondCut, thirdCut];
};

export const shrinkLabel = (label: InternalLabel, newStart: number, newEnd: number): InternalLabel => {
    const start = Math.max(label.originalLabel.start, newStart);
    const end = Math.min(label.originalLabel.end, newEnd);

    if (end - start >= 0) {
        const newLabel: InternalLabel = {
            ...label,
            children: [],
            originalLabel: { entityId: label.originalLabel.entityId, roleId: label.originalLabel.roleId, start, end }
        };

        label.children.forEach(childLabel => {
            const newChildLabel = shrinkLabel(childLabel, newStart, newEnd);
            if (newChildLabel) {
                newLabel.children.push(newChildLabel);
            }
        });

        return newLabel;
    }

    return null;
};

export const fromLabelStoreToInteractionsLabel = (label: EntityLabelStore): LabelingInteractionsEntityLabel => ({
    entityId: label.entityId.toString(),
    start: label.startTokenIndex,
    end: label.endTokenIndex,
    roleId: label.roleId?.toString()
});

export const fromInteractionsLabelToLabelStore = (label: LabelingInteractionsEntityLabel): EntityLabelStore =>
    createEntityLabelStore(
        label.entityId,
        { startTokenIndex: label.start, endTokenIndex: label.end, startCharIndex: null, endCharIndex: null },
        label.roleId
    );

export const toInteractionsLabel = (internalLabels: InternalLabel[]): LabelingInteractionsEntityLabel[] => {
    const queue: InternalLabel[] = internalLabels.slice();
    const labels: LabelingInteractionsEntityLabel[] = [];

    while (queue.length) {
        const item = queue.shift();
        labels.push(item.originalLabel);
        queue.push(...item.children);
    }

    return labels;
};

export const toInternalLabel = (state: InternalLabelKind) => (label: LabelingInteractionsEntityLabel): InternalLabel => ({
    children: [],
    originalLabel: <LabelingInteractionsEntityLabel>label,
    kind: state
});

export const getSchemaPath = (entityCollection: EntityCollection, entityId: string, path: string[] = []): string[] => {
    path.push(entityId);
    const entity = entityCollection.get(entityId);
    // Entity can be null in case it's a hierarchical child or composite child with role.
    if (!entity?.parentId) {
        return path;
    }

    const {
        entity: { id: parentEntityId }
    } = entityCollection.get(entity.parentId);

    return getSchemaPath(entityCollection, parentEntityId.toString(), path);
};

export const assignChildren = (labels: InternalLabel[], entityCollection: EntityCollection): void => {
    const toBeDeletedLabels: number[] = [];
    for (const parentLabel of labels) {
        const parentCollection = entityCollection.get(parentLabel.originalLabel.entityId);

        if (!parentCollection) {
            continue;
        }

        const parentEntity = parentCollection.entity;
        if (parentEntity.typeId === ModelType.Simple && parentEntity.children?.length > 0) {
            for (let j = 0; j < labels.length; j++) {
                const label = labels[j];
                if (isChildLabel(parentEntity, parentLabel, label)) {
                    parentLabel.children.push(label);
                    toBeDeletedLabels.push(j);
                }
            }
        }
    }

    toBeDeletedLabels.sort((a, b) => a - b);

    for (let i = toBeDeletedLabels.length - 1; i >= 0; i--) {
        labels.splice(toBeDeletedLabels[i], 1);
    }
};

/**
 * Returns whether the given label is a child to the parent label or not. By checking token indices and
 * the entity of the child. In case of Composite entity the child might have a role in this case its id
 * will be the id of the role.
 */
const isChildLabel = (parentEntity: LabelingInteractionsEntity, parentLabel: InternalLabel, childLabel: InternalLabel) => {
    const { children } = parentEntity;

    return (
        children.find(childEntity => childEntity.id === childLabel.originalLabel.entityId) &&
        parentLabel.originalLabel.start <= childLabel.originalLabel.start &&
        parentLabel.originalLabel.end >= childLabel.originalLabel.end
    );
};

export const buildEntityCollection = (entities: LabelingInteractionsEntity[]) => {
    const output: EntityCollection = new Map();
    buildEntityCollectionRecursive(entities, null, output);

    return output;
};

const buildEntityCollectionRecursive = (entities: LabelingInteractionsEntity[], parentId: string, output: EntityCollection) => {
    for (const entity of entities) {
        output.set(entity.id.toString(), { entity, parentId });
        if (entity.typeId === ModelType.Simple && entity.children) {
            buildEntityCollectionRecursive(entity.children, entity.id.toString(), output);
        }
    }
};

export const createAncestorLabels = (entityCollection: EntityCollection, label: InternalLabel): InternalLabel[] => {
    const output: InternalLabel[] = [];
    createAncestorLabelsRecursive(entityCollection, label, output);

    return output;
};

const createAncestorLabelsRecursive = (entityCollection: EntityCollection, label: InternalLabel, output: InternalLabel[] = []) => {
    output.push(label);
    if (!entityCollection.has(label.originalLabel.entityId)) {
        return;
    }

    const { parentId } = entityCollection.get(label.originalLabel.entityId);

    if (parentId !== null) {
        const { end, start } = label.originalLabel;
        const entityId = entityCollection.get(parentId).entity.id.toString();
        const parentLabel = toInternalLabel('inferred')({ start, end, entityId });

        createAncestorLabelsRecursive(entityCollection, parentLabel, output);
    }
};

export const attachHierarchicalParentIds = (labels: InternalLabel[], entities: LabelingInteractionsEntity[]): void => {
    firstLoop: for (const label of labels) {
        for (const entity of entities) {
            if (entity.typeId === ModelType.Hierarchical && entity.children) {
                if (
                    entity.id === label.originalLabel.entityId ||
                    entity.children.find(child => child.id === label.originalLabel.entityId)
                ) {
                    label.hierarchicalParentId = entity.id.toString();
                    continue firstLoop;
                }
            }
        }
    }
};
